import { erpForms, erpRouteTable } from '@/definitions/store';
import type { ErpForm, ErpRouteEntry } from '@/definitions/types';
import { resourcePermission } from '@/permissions';
import type { MasterdataResourceSpec } from './types';

export type { MasterdataFieldSpec, MasterdataFieldType, MasterdataResourceSpec } from './types';

/** A resource key is the key of its served form. */
export type MasterdataResourceKey = string;

/** The generic Stammdaten lists live below this path; a spec's `path` is what follows. */
const MASTERDATA_PATH = '/masterdata/';

/**
 * The catalogs approved in I-1529 use shared local draft creation — plus the
 * postal codes (I-2760) and the characteristic sets (#2475), the first
 * catalogues built on that flow after it landed. Every key here has a row in
 * the backend's `catalogrequirements/policy.json`
 * (`catalogCreatePolicyParity.test.ts`) and a `create` route in the route table
 * (`catalogCreateRoutes.test.ts`). This is client behaviour (which resources
 * use the flow); the routes' names and paths come from the route table.
 */
export const catalogDraftCreateKeys = [
  'countries',
  'states',
  'measureUnits',
  'payGrades',
  'routingGroups',
  'paymentTerms',
  'deliveryTerms',
  'shippingMethods',
  'departments',
  'languages',
  'productCategories',
  'productGroups',
  'colors',
  'salesDocumentTags',
  'documentLineTypes',
  'toolGroups',
  'machineGroups',
  'downtimeReasons',
  'postalCodes',
  'characteristicSets',
  'priorities',
  // The article catalogues with more than one required field (BLP/nx#2763).
  'hazardClasses',
  'priceUnits',
  'attributes',
] as const;

export type CatalogDraftCreateKey = (typeof catalogDraftCreateKeys)[number];

/**
 * The generic masterdata specs, composed from what the backend serves
 * (BLP/nx#3257): the form carries the spec as authored (`form.masterdata`), the
 * route table the list route and the default sort. One composition, here —
 * nothing of a spec is kept in the client.
 */
type SpecRegistry = {
  forms: Readonly<Record<string, ErpForm>>;
  routes: Readonly<Record<string, ErpRouteEntry>>;
  specs: readonly MasterdataResourceSpec[];
  byKey: ReadonlyMap<string, MasterdataResourceSpec>;
  draftCreate: Readonly<Record<string, MasterdataResourceSpec>>;
};

let registry: SpecRegistry | null = null;

function composeSpec(form: ErpForm, entry: ErpRouteEntry): MasterdataResourceSpec {
  const { masterdata } = form;
  const list = entry.list;
  if (!masterdata || !list) throw new Error(`form "${form.key}" is no generic masterdata screen`);
  if (!list.path.startsWith(MASTERDATA_PATH)) {
    throw new Error(`list route of "${form.key}" is not below ${MASTERDATA_PATH}: ${list.path}`);
  }
  if (!form.titleKey) throw new Error(`form "${form.key}" has no titleKey`);
  return {
    ...masterdata,
    key: form.key,
    titleKey: form.titleKey,
    path: list.path.slice(MASTERDATA_PATH.length),
    routeName: list.name,
    ...(entry.sort ? { initialSort: entry.sort } : {}),
  };
}

function specRegistry(): SpecRegistry {
  const forms = erpForms();
  const table = erpRouteTable();
  const routes = table.resources;
  if (registry && registry.forms === forms && registry.routes === routes) return registry;

  const specs: MasterdataResourceSpec[] = [];
  // The route table's `order` is the order the resources are listed in (the
  // settings pages of these screens): the served maps carry none.
  for (const key of table.order.filter((k) => k in forms)) {
    const form = forms[key]!;
    const entry = routes[key];
    // A form with a masterdata block and a list route is a generic screen.
    if (form.masterdata && entry?.list) specs.push(composeSpec(form, entry));
  }
  const byKey = new Map(specs.map((spec) => [spec.key, spec]));
  const draftCreate = Object.fromEntries(
    catalogDraftCreateKeys.map((key) => {
      const spec = byKey.get(key);
      if (!spec) throw new Error(`catalogDraftCreateKeys names "${key}", which has no spec`);
      return [key, spec];
    })
  );
  registry = { forms, routes, specs, byKey, draftCreate };
  return registry;
}

/** Every generic masterdata spec, in the route table's `order`. */
export function masterdataResourceSpecs(): readonly MasterdataResourceSpec[] {
  return specRegistry().specs;
}

/** One spec, or undefined when the key is no generic masterdata resource. */
export function findMasterdataResourceSpec(
  key: string | null | undefined
): MasterdataResourceSpec | undefined {
  return key ? specRegistry().byKey.get(key) : undefined;
}

/** One spec; throws on a key that is no generic masterdata resource. */
export function masterdataResourceSpec(key: string): MasterdataResourceSpec {
  const spec = findMasterdataResourceSpec(key);
  if (!spec) throw new Error(`no masterdata resource spec "${key}"`);
  return spec;
}

/** The specs of the draft-create catalogs, by key. */
export function catalogDraftCreateSpecs(): Readonly<Record<string, MasterdataResourceSpec>> {
  return specRegistry().draftCreate;
}

/**
 * Where a resource's list screen lives inside the ERP, and the right it asks for.
 * The route table (`router.ts`) and the Stammdaten targets of the command palette
 * (`cockpit/tileTargets.ts`) both take them from here, so the two cannot drift.
 */
export const masterdataResourceListPath = (spec: { path: string }): string =>
  `${MASTERDATA_PATH}${spec.path}`;

export const masterdataResourceReadPermission = (spec: { basePath: string }): string =>
  resourcePermission(spec.basePath, 'read');
